import type { AppProps } from 'next/app';
import { ThemeProvider } from 'styled-components';
import { SessionProvider } from 'utils/context/session-provider';
import GlobalStyle from '../themes/GlobalStyle';
import { getTheme } from '../themes/helpers';
import { LocaleProvider } from '../utils/locale/locale';
import { TestableProvider } from '../utils/testable/testable';
import {
  IS_DEVELOPMENT,
  IS_INTEGRATION,
  IS_PRODUCTION,
  IS_TEST,
} from 'utils/check-environment/check-environment';
import { LocalStorageKeys, getLocalStorage } from 'utils/localStorage';
import { initialiseDataDog } from 'utils/services/datadog/datadog-helpers';
import { AppLogger } from '@workivate/tho-web-shared';
import DataDogErrorBoundary from 'utils/services/datadog/DataDogErrorBoundary';
import { ErrorPageContent } from 'components/features/error-page/error-page-content/ErrorPageContent';

/*
Initialise Datadog - should be enabled by default for test and prod,
for development it's explicitly enabled via localStorage or query param.
*/
const dataDogEnabled = getLocalStorage(LocalStorageKeys.dataDogEnabled);

const shouldEnableByDefault = IS_PRODUCTION || IS_TEST;
const shouldRespectURLPreference = IS_DEVELOPMENT || IS_INTEGRATION;
const userWantsDataDog = dataDogEnabled && shouldRespectURLPreference;

if (shouldEnableByDefault || userWantsDataDog) {
  if (typeof window !== 'undefined') {
    initialiseDataDog();
  }
  AppLogger.info('Datadog initialised');
}

AppLogger.configure({ isProduction: IS_PRODUCTION });

// SessionProvider must be ancestor of LocaleProvider not descendant
// to avoid reinitialising session context on locale change
function UslApp({ Component, pageProps }: AppProps) {
  return (
    <DataDogErrorBoundary fallback={<ErrorPageContent />}>
      <ThemeProvider theme={getTheme()}>
        <TestableProvider>
          <SessionProvider>
            <LocaleProvider>
              <GlobalStyle />
              <Component {...pageProps} />
            </LocaleProvider>
          </SessionProvider>
        </TestableProvider>
      </ThemeProvider>
    </DataDogErrorBoundary>
  );
}

export default UslApp;
